import { Link, useSearchParams } from 'react-router-dom'

import { notierungenApi } from '../../api_notierungen'
import { useLaden } from '../../hooks'
import Delta, { SignalMarke } from '../../komponenten/Delta'
import Meldungen from '../../komponenten/Meldungen'
import { datum, eurT, heuteIso, liefermonat, zeitpunkt } from '../../format'
import { assistentPfad, notierungLaufPfad, notierungTabellePfad, notierungVerlaufPfad } from '../../pfade'
import type { NotierungGruppe, NotierungHeuteBestpreis, NotierungNeuesAngebot, NotierungRohstoff, NotierungenHeute } from '../../typen'

/**
 * „Heute" der Rohstoffnotierungen - die Seite für Herrn Schulz um die Mittagszeit: ist heute
 * Einkaufstag? Oben der Satz, dann die Einkaufssignale mit Liefermonat und Preis, wer schon notiert
 * hat und wer noch fehlt, die Bestpreise der nächsten Monate und die Läufe, die jemand ansehen muss.
 * Ohne KI - alles regelbasiert aus den Notierungen.
 */
export default function HeuteSeite() {
  const [suche, setSuche] = useSearchParams()
  const am = suche.get('am') ?? undefined
  const { daten: heute, fehler } = useLaden(() => notierungenApi.heute(am), [am])
  // Für die Links in die Tabelle und den Verlauf: zu welcher Gruppe und welcher Id ein Rohstoff gehört.
  const { daten: rohstoffe } = useLaden(() => notierungenApi.rohstoffe(), [])
  const { daten: gruppen } = useLaden(() => notierungenApi.gruppen(), [])

  return (
    <section>
      <div className="leiste">
        <h2>Heute</h2>
        <label className="hinweis">
          Stand vom{' '}
          <input
            type="date"
            value={am ?? heute?.am ?? heuteIso()}
            max={heuteIso()}
            onChange={(e) => setSuche(e.target.value && e.target.value !== heuteIso() ? { am: e.target.value } : {}, { replace: true })}
          />
        </label>
        {heute && (
          <Link className="knopf" to={assistentPfad(`Was heißt der Stand vom ${datum(heute.am)} für den Einkauf? ${heute.zusammenfassung}`, 'notierungen')}>
            Mit dem Assistenten besprechen
          </Link>
        )}
      </div>
      <Meldungen fehler={fehler} />
      {heute && (
        <>
          <p className="nz-satz">{heute.zusammenfassung}</p>
          <Statuszeile heute={heute} />
          <div className="nz-heute-raster">
            <Signale heute={heute} rohstoffe={rohstoffe ?? []} gruppen={gruppen ?? []} />
            <Eingang heute={heute} />
          </div>
          <NeueAngebote liste={heute.neue_angebote ?? []} />
          <Bestpreise liste={heute.bestpreise} rohstoffe={rohstoffe ?? []} gruppen={gruppen ?? []} />
          {heute.laeufe_pruefen.length > 0 && <ZurPruefung heute={heute} />}
        </>
      )}
    </section>
  )
}

/** Gruppe eines Rohstoffs (für den Link in die Tabelle) - aus den Gruppen, sonst aus den Rohstoffen. */
function gruppeVon(rohstoff: string, gruppen: NotierungGruppe[], rohstoffe: NotierungRohstoff[]): string | null {
  return gruppen.find((g) => g.rohstoffe.some((r) => r.name === rohstoff))?.gruppe ?? rohstoffe.find((r) => r.name === rohstoff)?.gruppe ?? null
}

function Statuszeile({ heute }: { heute: NotierungenHeute }) {
  const signale = heute.signale.length
  const starke = heute.signale.filter((s) => s.art === 'stark').length
  return (
    <div className="statuszeile">
      <a className={`kachel ${signale > 0 ? 'gut' : ''}`} href="#signale">
        <span className="kachel-zahl">{signale}</span>
        <span className="kachel-wort">
          {signale === 1 ? 'Einkaufssignal' : 'Einkaufssignale'}
          {starke > 0 ? ` - davon ${starke} stark` : ''}
        </span>
      </a>
      <a className="kachel" href="#eingang">
        <span className="kachel-zahl">{heute.eingegangen.length}</span>
        <span className="kachel-wort">{heute.eingegangen.length === 1 ? 'Lieferant hat notiert' : 'Lieferanten haben notiert'}</span>
      </a>
      {heute.ausstehend.length > 0 && (
        <a className="kachel" href="#eingang">
          <span className="kachel-zahl">{heute.ausstehend.length}</span>
          <span className="kachel-wort">noch ausstehend</span>
        </a>
      )}
      {(heute.neue_angebote ?? []).length > 0 && (
        <a className="kachel warnend" href="#neue-angebote">
          <span className="kachel-zahl">{heute.neue_angebote!.length}</span>
          <span className="kachel-wort">{heute.neue_angebote!.length === 1 ? 'neues Angebot ohne Spalte' : 'neue Angebote ohne Spalte'}</span>
        </a>
      )}
      {heute.laeufe_pruefen.length > 0 && (
        <Link className="kachel warnend" to="/notierungen/importe">
          <span className="kachel-zahl">{heute.laeufe_pruefen.length}</span>
          <span className="kachel-wort">{heute.laeufe_pruefen.length === 1 ? 'Lauf zur Prüfung' : 'Läufe zur Prüfung'}</span>
        </Link>
      )}
    </div>
  )
}

function Signale({ heute, rohstoffe, gruppen }: { heute: NotierungenHeute; rohstoffe: NotierungRohstoff[]; gruppen: NotierungGruppe[] }) {
  return (
    <div className="karte" id="signale">
      <h3>Einkaufssignale</h3>
      {heute.signale.length === 0 ? (
        <p className="hinweis">
          Kein Preis ist deutlich gefallen. Ein Signal kommt bei einem Tagesrückgang ab dem Wert unter Stammdaten · Signale oder
          bei einem Rückgang über mehrere Tage.
        </p>
      ) : (
        <div className="tabellenrahmen">
          <table>
            <thead>
              <tr>
                <th>Rohstoff · Spalte</th>
                <th>Liefermonat</th>
                <th className="zahl">Preis €/t</th>
                <th className="zahl">zu Vortag</th>
                <th>Signal</th>
              </tr>
            </thead>
            <tbody>
              {heute.signale.map((s) => {
                const gruppe = gruppeVon(s.rohstoff, gruppen, rohstoffe)
                const rohstoffId = rohstoffe.find((r) => r.name === s.rohstoff)?.id
                return (
                  <tr key={`${s.quelle_id}-${s.liefermonat}-${s.art}`}>
                    <td>
                      <b>{s.rohstoff}</b> · {s.bezeichnung}
                      <div className="hinweis">{s.text}</div>
                    </td>
                    <td>
                      {rohstoffId ? <Link to={notierungVerlaufPfad(rohstoffId, s.liefermonat)} title="Verlauf dieses Monats">{liefermonat(s.liefermonat)}</Link> : liefermonat(s.liefermonat)}
                    </td>
                    <td className="zahl" title={s.preis_inkl !== s.preis ? `netto ${eurT(s.preis)} €/t` : undefined}>
                      {eurT(s.preis_inkl ?? s.preis)}
                      {s.preis_inkl !== s.preis && <div className="hinweis">inkl. Transport</div>}
                    </td>
                    <td className="zahl" title={s.vortag_preis !== null ? `Vortag ${eurT(s.vortag_preis)} €/t` : undefined}>
                      <Delta wert={s.delta} inWorten />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <SignalMarke signal={s.art} />
                      {gruppe && (
                        <div>
                          <Link
                            className="hinweis-link"
                            to={notierungTabellePfad(gruppe, heute.am, { spalte: s.quelle_id, monate: [s.liefermonat] })}
                            title="In der Tabelle zeigen"
                          >
                            Tabelle
                          </Link>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Eingang({ heute }: { heute: NotierungenHeute }) {
  return (
    <div className="karte" id="eingang">
      <h3>Eingegangen und ausstehend</h3>
      {heute.eingegangen.length === 0 && heute.ausstehend.length === 0 && <p className="hinweis">Noch nichts gekommen.</p>}
      <ul className="nz-eingang">
        {heute.eingegangen.map((e) => (
          <li key={`${e.lieferant}-${e.lauf_id ?? ''}`}>
            <span className="status gebucht">da</span> <b>{e.lieferant}</b>
            {e.uhrzeit && <span className="hinweis"> um {e.uhrzeit} Uhr</span>}
            {e.notiert_am !== heute.am && <span className="hinweis"> (Notierung vom {datum(e.notiert_am)})</span>}
            <div className="hinweis">
              {e.quellen.join(', ')}
              {e.lauf_id !== null && (
                <>
                  {' · '}
                  <Link className="hinweis-link" to={notierungLaufPfad(e.lauf_id)}>
                    Lauf {e.lauf_id}
                  </Link>
                </>
              )}
            </div>
          </li>
        ))}
        {heute.ausstehend.map((a) => (
          <li key={`aus-${a.lieferant}`}>
            <span className="status">fehlt</span> <b>{a.lieferant}</b>
            <span className="hinweis">{a.sonst_um ? ` - kommt sonst gegen ${a.sonst_um} Uhr` : ' - notiert sonst regelmäßig'}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Bestpreise der nächsten Monate: je Rohstoff und Qualität eine Zeile, je Liefermonat eine Spalte. */
function Bestpreise({ liste, rohstoffe, gruppen }: { liste: NotierungHeuteBestpreis[]; rohstoffe: NotierungRohstoff[]; gruppen: NotierungGruppe[] }) {
  if (liste.length === 0) return null
  const monate = [...new Set(liste.map((b) => b.liefermonat))].sort()
  const zeilen = new Map<string, { rohstoff: string; qualitaet: string | null; werte: Map<string, NotierungHeuteBestpreis> }>()
  for (const b of liste) {
    const k = `${b.rohstoff}|${b.qualitaet ?? ''}`
    if (!zeilen.has(k)) zeilen.set(k, { rohstoff: b.rohstoff, qualitaet: b.qualitaet, werte: new Map() })
    zeilen.get(k)!.werte.set(b.liefermonat, b)
  }
  return (
    <div className="karte">
      <h3>Bestpreise der nächsten Monate</h3>
      <p className="hinweis" style={{ marginTop: 0 }}>Günstigste Spalte je Liefermonat, mit Transport gerechnet. Im Tooltip der Zweitbeste.</p>
      <div className="tabellenrahmen">
        <table className="nz-bestpreise">
          <thead>
            <tr>
              <th>Rohstoff</th>
              {monate.map((m) => (
                <th key={m} className="zahl">
                  {liefermonat(m)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...zeilen.values()].map((z) => {
              const gruppe = gruppeVon(z.rohstoff, gruppen, rohstoffe)
              return (
                <tr key={`${z.rohstoff}|${z.qualitaet ?? ''}`}>
                  <td>
                    {gruppe ? <Link to={notierungTabellePfad(gruppe)}>{z.rohstoff}</Link> : z.rohstoff}
                    {z.qualitaet && <span className="hinweis"> {z.qualitaet}</span>}
                  </td>
                  {monate.map((m) => {
                    const b = z.werte.get(m)
                    return (
                      <td
                        key={m}
                        className="zahl"
                        title={b?.zweiter ? `Zweitbester: ${b.zweiter.bezeichnung} ${eurT(b.zweiter.preis_inkl)} €/t (+${eurT(b.zweiter.preis_inkl - b.preis_inkl)})` : undefined}
                      >
                        {b && (
                          <>
                            <b>{eurT(b.preis_inkl)}</b>
                            <div className="hinweis">{b.bezeichnung}</div>
                          </>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function angebotFrage(a: NotierungNeuesAngebot): string {
  const merkmale = [a.qualitaet, a.ladeort && `ab ${a.ladeort}`, a.rohstoff].filter(Boolean).join(', ')
  return (
    `${a.lieferant ?? 'Ein Lieferant'} bietet in Lauf ${a.lauf_id} „${a.produkt}“${merkmale ? ` (${merkmale})` : ''} an, ` +
    `${a.monate} Monate mit Preis. Dafür gibt es keine Spalte. Was schlagen Sie vor - eine Spalte anlegen oder als nicht gebraucht merken?`
  )
}

/**
 * Was ein Lieferant anbietet, wofür es keine Spalte gibt und das keine Regel ausschließt (seit dem
 * 20.10.2026 - vorher fiel es still als „nicht gebraucht“ weg). Der Lauf wartet; mit dem Assistenten wird
 * daraus eine Spalte samt Regel oder eine Regel „nicht gebraucht“, und der Lauf ordnet sich neu zu.
 */
function NeueAngebote({ liste }: { liste: NotierungNeuesAngebot[] }) {
  if (liste.length === 0) return null
  return (
    <div className="karte datenpruefung" id="neue-angebote">
      <div className="leiste">
        <b>Neue Angebote ohne Spalte</b>
        <span className="hinweis">anlegen oder als nicht gebraucht merken - bitte besprechen</span>
      </div>
      <ul className="befund-liste">
        {liste.map((a) => (
          <li key={`${a.lauf_id}-${a.produkt}`} className="befund warnung">
            <span className="status warnung">Warnung</span>
            <div className="befund-text">
              <b>{a.lieferant}</b>: „{a.produkt}“
              <div className="hinweis">
                {[a.rohstoff, a.qualitaet, a.ladeort && `ab ${a.ladeort}`, `${a.monate} ${a.monate === 1 ? 'Monat' : 'Monate'} mit Preis`,
                  a.notiert_am && `notiert ${datum(a.notiert_am)}`]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              <div className="leiste">
                <Link className="knopf" to={assistentPfad(angebotFrage(a), 'notierungen')}>
                  Mit dem Assistenten besprechen
                </Link>
                <Link className="hinweis-link" to={notierungLaufPfad(a.lauf_id)}>
                  Lauf {a.lauf_id} ansehen
                </Link>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function laufFrage(l: NotierungenHeute['laeufe_pruefen'][number]): string {
  const gruende = l.gruende?.length ? l.gruende : l.grund ? [l.grund] : []
  return (
    `Lauf ${l.id} (${l.lieferant_text ?? l.dateiname ?? 'ohne Namen'}) wartet auf Prüfung. Gründe: ${gruende.join(' ')} ` +
    'Bitte sehen Sie ihn sich an und schlagen Sie vor, was wir tun.'
  )
}

function ZurPruefung({ heute }: { heute: NotierungenHeute }) {
  return (
    <div className="karte">
      <h3>Läufe zur Prüfung</h3>
      <p className="hinweis" style={{ marginTop: 0 }}>Diese Notierungen wurden nicht von selbst übernommen - der Grund steht dabei.</p>
      <ul className="nz-eingang">
        {heute.laeufe_pruefen.map((l) => (
          <li key={l.id}>
            <Link to={notierungLaufPfad(l.id)}>
              Lauf {l.id}: {l.lieferant_text ?? l.dateiname ?? 'ohne Namen'}
            </Link>
            <span className="hinweis"> - {zeitpunkt(l.angelegt_am)}</span>
            {' '}
            <Link className="hinweis-link" to={assistentPfad(laufFrage(l), 'notierungen')}>
              besprechen
            </Link>
            {l.grund && <div className="hinweis">{l.grund}</div>}
          </li>
        ))}
      </ul>
    </div>
  )
}
